import { useState, useEffect, useCallback } from 'react';
import { useChat } from '../../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { FormulaTooltip } from '../FormulaTooltip';
import { 
  BarChart, Bar, ScatterChart, Scatter, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend, ZAxis
} from 'recharts';
import { 
  Rocket,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Zap,
  Target,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Sparkles,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Pause,
  Play,
  BarChart3,
  Calendar,
  Coins,
  Wallet,
  Cake,
  Heart,
  ArrowUp,
  Activity,
  Gauge,
  AlertCircle,
  CheckCircle,
  ZoomIn
} from 'lucide-react';
import axios from 'axios';
import DrillDownModal from '../DrillDownModal';
import ChartZoomModal, { ChartZoomButton } from '../ChartZoomModal';
import DeepAIInsights from '../DeepAIInsights';
import DashboardFilters from '../DashboardFilters';

import { API_BASE_URL as API } from '../../utils/apiConfig';

// Campaign Type Icons Mapping
const CAMPAIGN_ICONS = {
  'coins': Coins,
  'wallet': Wallet,
  'cake': Cake,
  'calendar': Calendar,
  'users': Users,
  'arrow-up': ArrowUp,
  'heart': Heart,
  'zap': Zap
};

// ROI Speedometer Component
const ROISpeedometer = ({ value, size = 200 }) => {
  const normalizedValue = Math.min(Math.max(value, -100), 400); // Clamp to -100 to 400
  const angle = ((normalizedValue + 100) / 500) * 180; // Map to 0-180 degrees
  
  const getColor = (v) => {
    if (v >= 200) return '#10B981';
    if (v >= 100) return '#3B82F6';
    if (v >= 0) return '#F59E0B';
    return '#EF4444';
  };
  
  const radius = size / 2 - 20;
  const needleLength = radius - 10;
  const centerX = size / 2;
  const centerY = size / 2;
  
  // Calculate needle end point
  const needleAngle = (180 - angle) * (Math.PI / 180);
  const needleX = centerX + needleLength * Math.cos(needleAngle);
  const needleY = centerY - needleLength * Math.sin(needleAngle);

  return (
    <div className="relative" style={{ width: size, height: size / 2 + 40 }}>
      <svg width={size} height={size / 2 + 20}>
        {/* Background arc segments */}
        <defs>
          <linearGradient id="roiGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#EF4444" />
            <stop offset="20%" stopColor="#F59E0B" />
            <stop offset="40%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#10B981" />
          </linearGradient>
        </defs>
        
        {/* Track */}
        <path
          d={`M 20 ${centerY} A ${radius} ${radius} 0 0 1 ${size - 20} ${centerY}`}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="16"
          strokeLinecap="round"
        />
        
        {/* Colored arc based on value */}
        <path
          d={`M 20 ${centerY} A ${radius} ${radius} 0 0 1 ${size - 20} ${centerY}`}
          fill="none"
          stroke="url(#roiGradient)"
          strokeWidth="16"
          strokeLinecap="round"
          strokeDasharray={`${(angle / 180) * Math.PI * radius} ${Math.PI * radius}`}
        />
        
        {/* Needle */}
        <line
          x1={centerX}
          y1={centerY}
          x2={needleX}
          y2={needleY}
          stroke={getColor(value)}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle cx={centerX} cy={centerY} r="8" fill={getColor(value)} />
        <circle cx={centerX} cy={centerY} r="4" fill="white" />
        
        {/* Labels */}
        <text x="25" y={centerY + 20} fontSize="10" fill="hsl(var(--muted-foreground))">-100%</text>
        <text x={size - 40} y={centerY + 20} fontSize="10" fill="hsl(var(--muted-foreground))">400%</text>
      </svg>
      
      <div className="absolute inset-x-0 bottom-0 text-center">
        <span className="text-3xl font-bold" style={{ color: getColor(value) }}>{value.toFixed(0)}%</span>
        <p className="text-xs text-muted-foreground">Portfolio ROI</p>
      </div>
    </div>
  );
};

// Campaign Card Component
const CampaignCard = ({ campaign, onClick, isExpanded, onToggle }) => {
  const Icon = CAMPAIGN_ICONS[campaign.icon] || Zap;
  
  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(0)}L`;
    return `₹${value?.toLocaleString()}`;
  };

  const getStatusBadge = (rec) => {
    const icons = { SCALE: Play, OPTIMIZE: Target, MONITOR: Pause, KILL: ThumbsDown };
    const StatusIcon = icons[rec.action] || Activity;
    return (
      <div 
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
        style={{ backgroundColor: `${rec.color}20`, color: rec.color }}
      >
        <StatusIcon className="w-3.5 h-3.5" />
        {rec.action}
      </div>
    );
  };

  return (
    <Card className="overflow-hidden transition-all hover:shadow-lg">
      <CardContent className="p-0">
        {/* Header Row */}
        <div 
          className="flex items-center gap-4 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
          onClick={onToggle}
        >
          <div 
            className="p-2.5 rounded-xl"
            style={{ backgroundColor: `${campaign.color}20` }}
          >
            <Icon className="w-5 h-5" style={{ color: campaign.color }} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold truncate">{campaign.name}</h4>
              {campaign.fatigue?.is_fatigued && (
                <Badge variant="outline" className="text-amber-400 border-amber-400/30">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Fatigued
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{campaign.type} • {campaign.month}</p>
          </div>
          
          {/* Key Metrics */}
          <div className="hidden md:flex items-center gap-4">
            <div className="text-right">
              <FormulaTooltip metric="campaign_roi">
                <p className="text-xs text-muted-foreground">ROI</p>
              </FormulaTooltip>
              <p className={`text-lg font-bold ${campaign.metrics.roi >= 100 ? 'text-emerald-400' : campaign.metrics.roi >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                {campaign.metrics.roi > 0 ? '+' : ''}{campaign.metrics.roi.toFixed(0)}%
              </p>
            </div>
            <div className="text-right">
              <FormulaTooltip name="Lift" formula="(post_campaign_atv - pre_campaign_atv) ÷ pre_campaign_atv × 100" source="Live aggregation">
                <p className="text-xs text-muted-foreground">Lift</p>
              </FormulaTooltip>
              <p className="text-lg font-bold text-blue-400">+{campaign.metrics.actual_lift_pct.toFixed(0)}%</p>
            </div>
            <div className="text-right">
              <FormulaTooltip metric="incremental_revenue">
                <p className="text-xs text-muted-foreground">Incremental</p>
              </FormulaTooltip>
              <p className="text-lg font-bold">{formatCurrency(campaign.metrics.incremental_revenue)}</p>
            </div>
          </div>
          
          {getStatusBadge(campaign.recommendation)}
          
          <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
        
        {/* Expanded Details */}
        {isExpanded && (
          <div className="px-4 pb-4 pt-2 border-t border-border/50 bg-muted/20">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <div className="p-3 rounded-lg bg-background/50 text-center">
                <FormulaTooltip name="Exposed" formula="COUNT(DISTINCT recipient)  WHERE delivered = true" source="Karix delivery report">
                  <p className="text-xs text-muted-foreground mb-1">Exposed</p>
                </FormulaTooltip>
                <p className="text-lg font-bold">{campaign.metrics.exposed_customers?.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-background/50 text-center">
                <FormulaTooltip name="Converted" formula="COUNT(DISTINCT recipient)  WHERE recipient had a txn within 7d of delivery" source="Joined query">
                  <p className="text-xs text-muted-foreground mb-1">Converted</p>
                </FormulaTooltip>
                <p className="text-lg font-bold">{campaign.metrics.converted_customers?.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-background/50 text-center">
                <FormulaTooltip metric="conversion_rate">
                  <p className="text-xs text-muted-foreground mb-1">Conv. Rate</p>
                </FormulaTooltip>
                <p className="text-lg font-bold">{campaign.metrics.conversion_rate}%</p>
              </div>
              <div className="p-3 rounded-lg bg-background/50 text-center">
                <FormulaTooltip metric="visit_lift">
                  <p className="text-xs text-muted-foreground mb-1">Visit Lift</p>
                </FormulaTooltip>
                <p className="text-lg font-bold text-blue-400">+{campaign.metrics.visit_lift}%</p>
              </div>
              <div className="p-3 rounded-lg bg-background/50 text-center">
                <FormulaTooltip metric="spend_lift">
                  <p className="text-xs text-muted-foreground mb-1">Spend Lift</p>
                </FormulaTooltip>
                <p className="text-lg font-bold text-emerald-400">+{campaign.metrics.spend_lift}%</p>
              </div>
              <div className="p-3 rounded-lg bg-background/50 text-center">
                <FormulaTooltip name="Campaign Cost" formula="sms_cost + creative_cost" source="campaigns.cost">
                  <p className="text-xs text-muted-foreground mb-1">Cost</p>
                </FormulaTooltip>
                <p className="text-lg font-bold">{formatCurrency(campaign.metrics.campaign_cost)}</p>
              </div>
            </div>
            
            {/* Fatigue Indicator */}
            <div className="mt-3 flex items-center gap-3">
              <span className="text-xs text-muted-foreground">Fatigue Score:</span>
              <Progress value={campaign.fatigue?.score || 0} className="flex-1 h-2" />
              <span className="text-xs font-medium">{campaign.fatigue?.score || 0}/100</span>
              <span className="text-xs text-muted-foreground">({campaign.fatigue?.run_count || 0} runs)</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Campaign Type Performance Card
const TypePerformanceCard = ({ typeData, onClick }) => {
  const Icon = CAMPAIGN_ICONS[typeData.icon] || Zap;
  
  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(0)}L`;
    return `₹${value?.toLocaleString()}`;
  };

  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-all group"
      onClick={() => onClick(typeData)}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div 
            className="p-2 rounded-lg"
            style={{ backgroundColor: `${typeData.color}20` }}
          >
            <Icon className="w-5 h-5" style={{ color: typeData.color }} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-sm">{typeData.type}</h4>
            <p className="text-xs text-muted-foreground">{typeData.campaign_count} campaigns</p>
          </div>
          <div 
            className="text-xl font-bold"
            style={{ color: typeData.avg_roi >= 100 ? '#10B981' : typeData.avg_roi >= 0 ? '#F59E0B' : '#EF4444' }}
          >
            {typeData.avg_roi > 0 ? '+' : ''}{typeData.avg_roi}%
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-muted-foreground">Avg Lift:</span>
            <span className="ml-1 font-medium">+{typeData.avg_lift}%</span>
          </div>
          <div>
            <span className="text-muted-foreground">Efficiency:</span>
            <span className="ml-1 font-medium">{typeData.efficiency_score}%</span>
          </div>
        </div>
        
        <div className="mt-2 pt-2 border-t border-border/50 flex justify-between text-xs text-muted-foreground">
          <span>Cost: {formatCurrency(typeData.total_cost)}</span>
          <span>Revenue: {formatCurrency(typeData.total_incremental)}</span>
        </div>
        
        <ChevronRight className="w-4 h-4 absolute bottom-4 right-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
      </CardContent>
    </Card>
  );
};

// Status Distribution Visual
const StatusDistribution = ({ data }) => {
  const total = data.reduce((acc, d) => acc + d.count, 0);
  
  return (
    <div className="space-y-2">
      {data.map((item, idx) => (
        <div key={idx} className="flex items-center gap-3">
          <span className="w-20 text-xs font-medium">{item.status}</span>
          <div className="flex-1 h-6 rounded-full bg-muted overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
              style={{ 
                width: `${(item.count / total) * 100}%`,
                backgroundColor: item.color,
                minWidth: item.count > 0 ? '40px' : '0'
              }}
            >
              <span className="text-xs font-bold text-white">{item.count}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function CampaignROIDashboard() {
  const { selectedBrand } = useChat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // overview, campaigns, types, scatter
  const [drillDown, setDrillDown] = useState({ open: false, type: null, data: null, title: '' });
  const [isLiveData, setIsLiveData] = useState(false);
  const [filters, setFilters] = useState({ dateRange: 'lifetime', city: 'all', store: 'all', roiDays: 3 });
  const [filterOptions, setFilterOptions] = useState({ cities: [], stores: [] });
  const [chartZoom, setChartZoom] = useState({ open: false, type: '', data: [], title: '', config: {} });

  // ROI timeframe options
  const roiTimeframeOptions = [
    { value: 3, label: '72 Hours (Default)' },
    { value: 5, label: '5 Days' },
    { value: 7, label: '7 Days' },
    { value: 10, label: '10 Days' },
    { value: 15, label: '15 Days' },
    { value: 30, label: '30 Days' }
  ];

  // Chart zoom handlers
  const openChartZoom = (chartType, chartData, chartTitle, chartConfig = {}) => {
    setChartZoom({ open: true, type: chartType, data: chartData, title: chartTitle, config: chartConfig });
  };
  const closeChartZoom = () => setChartZoom({ open: false, type: '', data: [], title: '', config: {} });

  // Check for live data
  const getDbBrandId = useCallback(() => {
    return selectedBrand?.db_brand_id || selectedBrand?.settings?.db_brand_id || null;
  }, [selectedBrand]);

  const fetchData = useCallback(async () => {
    if (!selectedBrand) return;
    
    setLoading(true);
    setError(null);
    
    // Check for live Data Bridge - mark as live if brand has db_brand_id
    const dbBrandId = getDbBrandId();
    // Live status set after response
    
    // Use original API for proper data structure
    try {
      const response = await axios.post(
        `${API}/api/analytics/campaign-roi`,
        { brand_id: selectedBrand.brand_id, filters },
        { withCredentials: true }
      );
      
      setData(response.data.data);
      setIsLiveData(response.data.data?.source === 'live_data_bridge' || !!dbBrandId);
      
      // Extract filter options from response
      if (response.data.data?.filter_context) {
        setFilterOptions({
          cities: response.data.data.filter_context.cities || [],
          stores: response.data.data.filter_context.stores || []
        });
      }
    } catch (err) {
      console.error('Campaign ROI data error:', err);
      setError('Failed to load campaign data');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, getDbBrandId, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDrillDown = (type, value, title, context = {}) => {
    setDrillDown({ open: true, type, data: { value, context }, title });
  };

  const closeDrillDown = () => {
    setDrillDown({ open: false, type: null, data: null, title: '' });
  };

  const handleExport = () => {
    if (!data) return;
    const csvContent = [
      ['Campaign', 'Type', 'Status', 'Sent', 'Converted', 'Revenue', 'ROI'],
      ...(data.campaigns || []).map(c => [c.name, c.type, c.status, c.sent, c.converted, c.revenue_attributed, c.roi])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaign_roi_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    return `₹${value?.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="campaign-roi-loading">
        <div className="flex flex-col items-center gap-4">
          <Rocket className="w-12 h-12 text-primary animate-pulse" />
          <p className="text-muted-foreground">Analyzing campaign performance...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={fetchData}>Retry</Button>
        </Card>
      </div>
    );
  }

  const summary = data?.summary || {};
  const campaigns = data?.campaigns || [];
  const typeAnalysis = data?.campaign_type_analysis || [];
  const scatterData = data?.scatter_data || [];
  const statusDistribution = data?.status_distribution || [];
  const cohortEffectiveness = data?.cohort_effectiveness || [];
  const insights = data?.insights || {};

  return (
    <div className="space-y-6" data-testid="campaign-roi-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              Campaign Lift & ROI
            </h2>
            {data?.is_simulated && (
              <Badge variant="outline" className="text-amber-400 border-amber-400/30">
                Simulated Data
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">Measure campaign effectiveness and optimize marketing spend</p>
        </div>
        
        <div className="flex items-center bg-muted rounded-lg p-1">
          <Button 
            variant={viewMode === 'overview' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('overview')}
          >
            Overview
          </Button>
          <Button 
            variant={viewMode === 'campaigns' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('campaigns')}
          >
            Campaigns
          </Button>
          <Button 
            variant={viewMode === 'types' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('types')}
          >
            By Type
          </Button>
          <Button 
            variant={viewMode === 'scatter' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('scatter')}
          >
            Lift vs Cost
          </Button>
        </div>
      </div>

      {/* Dashboard Filters */}
      <DashboardFilters
        filters={filters}
        setFilters={setFilters}
        filterOptions={filterOptions}
        onRefresh={fetchData}
        onExport={handleExport}
        loading={loading}
      />

      {/* ROI Timeframe Selection */}
      <div className="flex items-center gap-4 mb-4">
        <span className="text-sm text-muted-foreground">ROI Calculation Window:</span>
        <select
          className="bg-background border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          value={filters.roiDays}
          onChange={(e) => setFilters(prev => ({ ...prev, roiDays: parseInt(e.target.value) }))}
        >
          {roiTimeframeOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <span className="text-xs text-muted-foreground">
          (Customers who shopped within {filters.roiDays} days of campaign)
        </span>
      </div>

      {/* ROI Speedometer + Summary Stats */}
      <div className="grid lg:grid-cols-4 gap-6">
        {/* Speedometer */}
        <Card className="lg:col-span-1 flex items-center justify-center">
          <ROISpeedometer value={summary.avg_roi || 0} size={220} />
        </Card>
        
        {/* Summary Stats */}
        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: 'Campaigns', value: summary.total_campaigns, icon: Rocket, color: '#8B5CF6' },
            { label: 'Incremental Revenue', value: formatCurrency(summary.total_incremental_revenue), icon: TrendingUp, color: '#10B981' },
            { label: 'Total Spend', value: formatCurrency(summary.total_campaign_cost), icon: DollarSign, color: '#F59E0B' },
            { label: 'Avg Conversion', value: `${summary.avg_conversion_rate}%`, icon: Users, color: '#3B82F6' },
            { label: 'Avg Lift', value: `+${summary.avg_lift}%`, icon: Zap, color: '#EC4899' },
            { label: 'Fatigued', value: summary.fatigued_count, icon: AlertTriangle, color: '#EF4444', alert: summary.fatigued_count > 3 }
          ].map((stat, idx) => (
            <Card 
              key={idx} 
              className={`cursor-pointer hover:shadow-lg transition-all ${stat.alert ? 'border-amber-500/50' : ''}`}
              onClick={() => openDrillDown('kpi', stat.label.toLowerCase().replace(/\s+/g, '_'), stat.label)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <stat.icon className="w-4 h-4" style={{ color: stat.color }} />
                  <span className="text-xs text-muted-foreground">{stat.label}</span>
                </div>
                <p className="text-xl font-bold">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Status Distribution + Recommendations */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="w-4 h-4 text-blue-500" />
              Campaign Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StatusDistribution data={statusDistribution} />
            <div className="mt-4 grid grid-cols-2 gap-2 text-center">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <p className="text-2xl font-bold text-emerald-400">{summary.scale_count}</p>
                <p className="text-xs text-muted-foreground">Ready to Scale</p>
              </div>
              <div className="p-2 rounded-lg bg-red-500/10">
                <p className="text-2xl font-bold text-red-400">{summary.kill_count}</p>
                <p className="text-xs text-muted-foreground">Should Kill</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cohort Effectiveness */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-violet-500" />
              Cohort Response Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={cohortEffectiveness} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" domain={[0, 50]} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="cohort" width={80} tick={{ fontSize: 11 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="avg_response_rate" name="Response Rate %" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                <Bar dataKey="avg_spend_lift" name="Spend Lift %" fill="#10B981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* AI Insights Row */}
      <div className="grid md:grid-cols-3 gap-4">
        {[insights.best_performer, insights.underperformer, insights.fatigue_alert].filter(Boolean).map((insight, idx) => (
          <Card 
            key={idx}
            className={`cursor-pointer hover:shadow-lg transition-all bg-gradient-to-br ${
              insight?.severity === 'success' ? 'from-emerald-500/10 to-teal-500/5 border-emerald-500/20' :
              insight?.severity === 'warning' ? 'from-amber-500/10 to-orange-500/5 border-amber-500/20' :
              insight?.severity === 'critical' ? 'from-red-500/10 to-pink-500/5 border-red-500/20' :
              'from-blue-500/10 to-cyan-500/5 border-blue-500/20'
            }`}
            onClick={() => openDrillDown('insight', insight?.title, insight?.title)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${
                  insight?.severity === 'success' ? 'bg-emerald-500/20' :
                  insight?.severity === 'warning' ? 'bg-amber-500/20' :
                  insight?.severity === 'critical' ? 'bg-red-500/20' :
                  'bg-blue-500/20'
                }`}>
                  {insight?.severity === 'success' ? <CheckCircle className="w-5 h-5 text-emerald-400" /> :
                   insight?.severity === 'warning' ? <AlertTriangle className="w-5 h-5 text-amber-400" /> :
                   insight?.severity === 'critical' ? <AlertCircle className="w-5 h-5 text-red-400" /> :
                   <Sparkles className="w-5 h-5 text-blue-400" />}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-sm mb-1">{insight?.title}</h4>
                  <p className="text-xs text-muted-foreground">{insight?.insight}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Based on View Mode */}
      {viewMode === 'overview' && (
        <div className="space-y-4">
          {/* Top 5 Campaigns */}
          <h3 className="font-semibold flex items-center gap-2">
            <Rocket className="w-4 h-4" />
            Recent Campaigns
          </h3>
          {campaigns.slice(0, 5).map(campaign => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              isExpanded={expandedCampaign === campaign.id}
              onToggle={() => setExpandedCampaign(expandedCampaign === campaign.id ? null : campaign.id)}
            />
          ))}
        </div>
      )}

      {viewMode === 'campaigns' && (
        <div className="space-y-3">
          {campaigns.map(campaign => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              isExpanded={expandedCampaign === campaign.id}
              onToggle={() => setExpandedCampaign(expandedCampaign === campaign.id ? null : campaign.id)}
            />
          ))}
        </div>
      )}

      {viewMode === 'types' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {typeAnalysis.map(typeData => (
            <TypePerformanceCard
              key={typeData.type}
              typeData={typeData}
              onClick={(t) => openDrillDown('campaign_type', t.type, `${t.type} Analysis`)}
            />
          ))}
        </div>
      )}

      {viewMode === 'scatter' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-violet-500" />
              Lift vs Cost Analysis (Bubble size = Incremental Revenue)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  type="number" 
                  dataKey="cost" 
                  name="Cost" 
                  tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`}
                  tick={{ fontSize: 11 }}
                  label={{ value: 'Campaign Cost', position: 'bottom', fontSize: 12 }}
                />
                <YAxis 
                  type="number" 
                  dataKey="lift" 
                  name="Lift" 
                  tick={{ fontSize: 11 }}
                  label={{ value: 'Revenue Lift %', angle: -90, position: 'left', fontSize: 12 }}
                />
                <ZAxis type="number" dataKey="size" range={[50, 400]} />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ payload }) => {
                    if (!payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="bg-popover border rounded-lg p-3 shadow-lg">
                        <p className="font-semibold">{d.name}</p>
                        <p className="text-xs text-muted-foreground">{d.type}</p>
                        <div className="mt-2 space-y-1 text-xs">
                          <p>Cost: {formatCurrency(d.cost)}</p>
                          <p>Lift: +{d.lift.toFixed(1)}%</p>
                          <p>ROI: {d.roi > 0 ? '+' : ''}{d.roi.toFixed(0)}%</p>
                        </div>
                      </div>
                    );
                  }}
                />
                <Scatter data={scatterData}>
                  {scatterData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} fillOpacity={0.7} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-4 mt-2 text-xs text-muted-foreground">
              <span>💡 Top-left = High efficiency (high lift, low cost)</span>
              <span>⚠️ Bottom-right = Low efficiency (low lift, high cost)</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overall Insight */}
      <Card className="bg-gradient-to-r from-emerald-500/10 via-teal-500/5 to-cyan-500/10 border-emerald-500/20">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold mb-2">{insights.overall?.title}</h3>
              <p className="text-sm text-muted-foreground">{insights.overall?.insight}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deep AI Insights - FundleBrain Chief Intelligence Engine */}
      <DeepAIInsights
        brandId={selectedBrand?.brand_id}
        brandName={selectedBrand?.name}
        dashboardType="campaign_roi"
        dashboardData={data}
        kpis={{
          total_customers: { value: data?.totals?.total_customers || 0 },
          total_revenue: { value: data?.totals?.total_incremental || 0 }
        }}
        campaigns={data?.campaigns || []}
      />

      {/* Drill Down Modal */}
      <DrillDownModal
        open={drillDown.open}
        onClose={closeDrillDown}
        drillType={drillDown.type}
        drillData={drillDown.data}
        title={drillDown.title}
        brandId={selectedBrand?.brand_id}
      />

      {/* Chart Zoom Modal */}
      <ChartZoomModal
        open={chartZoom.open}
        onClose={closeChartZoom}
        chartType={chartZoom.type}
        chartData={chartZoom.data}
        chartTitle={chartZoom.title}
        chartConfig={chartZoom.config}
        brandId={selectedBrand?.brand_id}
        dashboardType="campaign_roi"
      />
    </div>
  );
}
